/** 集装箱/柜档案相关类型 */

import type { NodeSubstepKey } from './node-substep';

/** 集装箱类型 */
export enum ContainerType {
  REEFER_20 = 'REEFER_20',
  REEFER_40 = 'REEFER_40',
  DRY_20 = 'DRY_20',
  DRY_40 = 'DRY_40',
}

/** 集装箱状态 */
export enum ContainerStatus {
  AT_ORIGIN = 'AT_ORIGIN',
  IN_TRANSIT = 'IN_TRANSIT',
  AT_CUSTOMS = 'AT_CUSTOMS',
  AT_DESTINATION = 'AT_DESTINATION',
  DELIVERED = 'DELIVERED',
}

/** 节点状态 */
export enum NodeStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ALERT = 'ALERT',
  SKIPPED = 'SKIPPED',
}

/** 地理位置 */
export interface GeoLocation {
  lat: number;
  lng: number;
  portName: string;
  country: string;
}

/** 节点记录 */
export interface NodeRecord {
  nodeId: number;
  nodeName: string;
  status: NodeStatus;
  operatorId: string;
  operatorName: string;
  timestamp: string;
  photos: string[];
  notes: string;
}

/** 节点进度状态（用于UI进度条展示） */
export type NodeProgressStatus = 'done' | 'current' | 'pending' | 'alert';

/** 日志状态 */
export type LogStatus = 'normal' | 'warning' | 'danger';

/** 进度条节点 */
export interface ProgressNode {
  nodeId: number;
  shortName: string;
  fullName: string;
  status: NodeProgressStatus;
}

/** 节点流转日志 */
export interface ContainerArchiveLog {
  id: string;
  containerId: string;
  nodeCode: string;
  timestamp: string;
  operatorName: string;
  nodeName: string;
  description: string;
  logStatus: LogStatus;
}

/**
 * 节点时间线上的单条事件（在所属节点块内作为子行展示）
 *
 * <p>时间线的主轴是「节点」而非「事件」：事件按 `enterNode`（= to_node_code）归属到节点。
 * `leaveNode`（= from_node_code）**不**用于本条事件的展示位置，只用于计算来源节点的「离开时间」——
 * 因为「离开 A」这件事发生在推进到 B 的那一刻，其日志行的归属节点是 B，只能在 B 的组里看到 from=A。
 */
export interface NodeTimelineEvent {
  id: string;
  /** 后端动作：CREATE / ADVANCE / REVERT / JUMP / BLOCK / COMPLETE / DOC_CHANGE */
  action: string;
  /** 事件发生时间 */
  at: string;
  /** 归属节点（= to_node_code；单端事件回落 from_node_code） */
  enterNode: string;
  /** 来源节点（= from_node_code；建单等无来源事件为空） */
  leaveNode?: string;
  operatorName: string;
  /** 已渲染好的子行文案 */
  text: string;
  status: LogStatus;
}

/**
 * 节点时间线的一个节点块（时间线主行）
 *
 * <p>附件按其 `nodeCode` 挂在所属节点块内**只渲染一次**——避免「同一节点多条事件行各渲一份」。
 */
export interface NodeTimelineBlock {
  nodeCode: string;
  nodeName: string;
  /** 节点序号（来自 nodes 表）。排序依据是它而非时间，避免回退/跳转导致展示顺序错乱 */
  nodeSeq: number;
  status: 'DONE' | 'ACTIVE';
  /** 进入时间 = 归属本节点事件的最早一条 */
  enteredAt?: string;
  /** 离开时间 = 「离开本节点」事件的最早一条（该事件归属在下一个节点，故须跨组取） */
  leftAt?: string;
  /** 归属本节点的事件子行（按时间升序） */
  events: NodeTimelineEvent[];
}

/** 预警项 */
export interface AlertItem {
  containerNo: string;
  archiveNo: string;
  alertReason: string;
}

/** 预警汇总 */
export interface ContainerAlertSummary {
  count: number;
  items: AlertItem[];
}

/** 集装箱 */
export interface Container {
  id: string;
  containerNo: string;
  orderId: string;
  type: ContainerType;
  sealNo: string;
  currentNode: number;
  totalNodes: number;
  nodeHistory: NodeRecord[];
  iotDeviceId: string;
  temperature: number;
  humidity: number;
  location: GeoLocation;
  status: ContainerStatus;
  vesselName: string;
  voyageNo: string;
  estimatedArrival: string;
  actualArrival: string;
  /** 档案号 */
  archiveNo: string;
  /** 品类 */
  category: string;
  /** 航线 */
  route: string;
  /** 当前节点停留时长 */
  remainingStay: string;
  /**
   * 订舱号 / S-O（与 containerNo 区分）。
   * 订舱阶段(S/O)可先预填，提空柜后柜号再补入同一档案。
   */
  bookingNo?: string;
  /**
   * N4 当前所处子步骤（4.1→4.2→4.3）。仅在 currentNode===4 时有意义。
   */
  currentSubstep?: NodeSubstepKey;
}
